import React, { createContext, useContext, useReducer, useMemo, ReactNode } from 'react';
import { User, UserPreferences, UserStats } from '../types';
import { toast } from 'react-hot-toast';
import { authService } from '../utils/authService';

interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type UserAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<UserPreferences> }
  | { type: 'UPDATE_STATS'; payload: Partial<UserStats> }
  | { type: 'UPDATE_PROFILE'; payload: { username?: string; email?: string; bio?: string; country?: string } }
  | { type: 'ADD_XP'; payload: number }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_PREMIUM_STATUS'; payload: { isPremium: boolean | 'monthly' | 'yearly' | 'lifetime'; premiumExpiresAt?: string } };

const initialState: UserState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const STORAGE_KEY = 'typeRushUser_v2';

const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true, error: null };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload,
        error: null,
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        error: null,
      };
    
    case 'UPDATE_PREFERENCES':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          preferences: { ...state.user.preferences, ...action.payload },
        },
      };
    
    case 'UPDATE_STATS':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          stats: { ...state.user.stats, ...action.payload },
        },
      };
    
    case 'UPDATE_PROFILE':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
        },
      };
    
    case 'ADD_XP': {
      if (!state.user) return state;
      const newXp = state.user.xp + action.payload;
      const newTotalXp = state.user.totalXp + action.payload;
      const newLevel = Math.floor(newTotalXp / 1000) + 1;
      
      return {
        ...state,
        user: {
          ...state.user,
          xp: newXp,
          totalXp: newTotalXp,
          level: newLevel,
        },
      };
    }
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'UPDATE_PREMIUM_STATUS':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          isPremium: action.payload.isPremium,
        },
      };
    
    default:
      return state;
  }
};

interface UserContextType {
  state: UserState;
  login: (email: string, password: string) => Promise<void>;
  loginWithUser: (user: User) => void;
  logout: () => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
  updateStats: (stats: Partial<UserStats>) => void;
  updateProfile: (profile: { username?: string; email?: string; bio?: string; country?: string }) => void;
  addXp: (amount: number) => void;
  clearError: () => void;
  updatePremiumStatus: (isPremium: boolean | 'monthly' | 'yearly' | 'lifetime', premiumExpiresAt?: string) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Restore session from localStorage on startup
  React.useEffect(() => {
    try {
      const savedUser = localStorage.getItem(STORAGE_KEY);
      if (savedUser) {
        const user = JSON.parse(savedUser) as User;
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync localStorage whenever user state changes
  React.useEffect(() => {
    if (state.user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.user));
    }
  }, [state.user]);

  // Login with an already-resolved User object (used after successful authService calls)
  const loginWithUser = (user: User) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: user });
  };

  const login = async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const user = await authService.login(email, password);
      if (!user) throw new Error('Login failed');
      loginWithUser(user);
    } catch (error: any) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message || 'Login failed' });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    dispatch({ type: 'LOGOUT' });
  };

  const updatePreferences = (preferences: Partial<UserPreferences>) => {
    // Add any theme-specific validation here if needed
    // For example, block premium themes for non-premium users
    if (preferences.theme === 'matrix' || preferences.theme === 'neon') {
      if (!state.user?.isPremium) {
        return; // Don't update if not premium
      }
    }
    
    if (preferences.theme === 'ember') {
      if (state.user?.isPremium !== 'lifetime') {
        return; // Don't update if not lifetime premium
      }
    }
    
    dispatch({ type: 'UPDATE_PREFERENCES', payload: preferences });
  };

  const updateStats = (stats: Partial<UserStats>) => {
    dispatch({ type: 'UPDATE_STATS', payload: stats });
  };

  const updateProfile = (profile: { username?: string; email?: string; bio?: string; country?: string }) => {
    dispatch({ type: 'UPDATE_PROFILE', payload: profile });
  };

  const addXp = (amount: number) => {
    dispatch({ type: 'ADD_XP', payload: amount });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Update premium status after successful payment
  const updatePremiumStatus = (status: boolean | 'monthly' | 'yearly' | 'lifetime', expiresAt?: string) => {
    dispatch({
      type: 'UPDATE_PREMIUM_STATUS',
      payload: {
        isPremium: status,
        premiumExpiresAt: expiresAt
      }
    });

    const premiumType = typeof status === 'boolean' ? 'Premium' : status.charAt(0).toUpperCase() + status.slice(1);
    toast.success(`Your account has been upgraded to ${premiumType} status!`, {
      duration: 4000,
      icon: '🎉'
    });
  };

  const value: UserContextType = useMemo(() => ({
    state,
    login,
    loginWithUser,
    logout,
    updatePreferences,
    updateStats,
    updateProfile,
    addXp,
    clearError,
    updatePremiumStatus,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
